import dayjs from 'dayjs';
import { ProfileFieldItem } from '@/src/models/shared/grid-type';
import { EBloodTypeLabel } from '../enums/blood-type';
import { EGender, EGenderLabel } from '../enums/gender';
import { EMaritalStatus, EMaritalStatusLabel } from '../enums/marital-status';
import { EReligion, EReligionLabel } from '../enums/religion';

/**
 * @deprecated uses function from `@/src/datetime.ts` instead.
 */
export const formatCreatedAt = (date: string) => {
  return dayjs(date).format('DD MMM YYYY • HH:mm');
};

/**
 * @deprecated uses function from `@/src/datetime.ts` instead.
 */
export const formatTime = (time: string) => {
  return dayjs(time).format('HH:mm') ?? '-';
};

export const formatCurrency = (value: string | number) => {
  if (typeof value === 'string' && value.includes('Rp')) {
    return value;
  }

  let numericValue: number;
  if (typeof value === 'string') {
    // If it's a standard float string like "10000.00", parseFloat works directly.
    // If it has commas/dots as separators, we need to normalize it.
    if (/^\d+\.\d+$/.test(value)) {
      numericValue = parseFloat(value);
    } else {
      const cleaned = value.replace(/[^0-9,.-]+/g, '');
      const normalized = cleaned.replace(/\./g, '').replace(/,/g, '.');
      numericValue = parseFloat(normalized);
    }
  } else {
    numericValue = value;
  }

  const tmpValue = isNaN(numericValue) ? 0 : numericValue;

  return tmpValue.toLocaleString('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

export function withQuery<T extends Record<string, any>>(
  url: string,
  params?: T
) {
  if (!params || Object.keys(params).length === 0) {
    return url;
  }

  const isAbsolute = /^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(url);

  const base = isAbsolute
    ? url
    : `http://dummy${url.startsWith('/') ? url : `/${url}`}`;

  const urlObj = new URL(base);

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) {
      continue;
    }

    urlObj.searchParams.set(
      key,
      typeof value === 'object' ? JSON.stringify(value) : String(value)
    );
  }

  if (isAbsolute) {
    return urlObj.toString();
  }

  return urlObj.pathname + urlObj.search;
}

export const calculateAge = (birthdate: string | null) => {
  if (!birthdate) {
    return '-';
  }
  const today = dayjs();
  const birthDate = dayjs(birthdate);
  const age = today.diff(birthDate, 'year');
  return age;
};

export function toTitleCase(str: string) {
  return str.replace(
    /\w\S*/g,
    (text: string) =>
      text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
  );
}

export function splitFullName(fullName: string) {
  const parts = fullName.trim().split(/\s+/);

  return {
    first_name: parts[0] ?? '',
    last_name: parts.slice(1).join(' ') || '',
  };
}

export function formatEstimate(estimate: string): string {
  const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'Mei',
    'Jun',
    'Jul',
    'Agu',
    'Sep',
    'Okt',
    'Nov',
    'Des',
  ];
  const nums = estimate.match(/\d+/g)?.map(Number) ?? [0];
  const lowest = Math.min(...nums);
  const highest = Math.max(...nums);

  const toLabel = (days: number) => {
    if (days === 0) {
      return 'Hari ini';
    }
    const date = new Date();
    date.setDate(date.getDate() + days);
    return `${date.getDate()} ${MONTHS[date.getMonth()]}`;
  };

  const minLabel = toLabel(lowest);
  const maxLabel = toLabel(highest);
  return minLabel === maxLabel ? minLabel : `${minLabel} - ${maxLabel}`;
}

export function getEnumLabel<T extends string | number>(
  value: T | null | undefined,
  map: Record<T, string>,
  fallback = 'Tidak Diketahui'
): string {
  if (value == null) {
    return fallback;
  }
  return map[value] ?? fallback;
}

export function getEnumValue<T extends string | number>(
  label: string | null | undefined,
  map: Record<T, string>,
  fallback: T | null = null
): T | null {
  if (!label) {
    return fallback;
  }
  const normalized = label.trim().toLowerCase();
  const entry = (Object.entries(map) as [T, string][]).find(
    ([_, v]) => v.trim().toLowerCase() === normalized
  );
  return entry ? (entry[0] as T) : fallback;
}

export function enumToOptions<T extends string | number>(
  enumObj: Record<string, T>,
  labelMap: Record<T, string>
) {
  return Object.values(enumObj)
    .filter((v): v is T => typeof v === 'number')
    .map((value) => ({
      value: String(value),
      label: labelMap[value] ?? String(value),
    }));
}

const normalize = (str?: string) =>
  str
    ?.toLowerCase()
    .trim()
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ') ?? '';

export function findValueFromOption(
  label: string | undefined,
  options: { value: string; label: string }[]
): string {
  if (!label) {
    return '';
  }

  const target = normalize(label);

  const match = options.find((opt) => normalize(opt.label) === target);

  return match?.value ?? '';
}

export const BASE_IDENTITY_FIELDS = (
  fullName?: string,
  individual?: any
): ProfileFieldItem[] => [
  {
    key: 'nickname',
    label: 'Nama Lengkap',
    value: fullName?.trim(),
    disabled: false,
    type: 'text',
  },
  {
    key: 'nik',
    label: 'NIK',
    value: individual?.props.nik,
    disabled: false,
    type: 'text',
  },
  {
    key: 'birthdate',
    label: 'Tanggal Lahir',
    value: individual?.props.birthDate ?? null,
    disabled: false,
    type: 'date',
  },
  {
    key: 'birthplace',
    label: 'Tempat Lahir',
    value: individual?.props.birthplace,
    disabled: false,
    type: 'text',
  },
  {
    key: 'gender',
    label: 'Jenis Kelamin',
    value: String(individual?.props.gender),
    disabled: false,
    type: 'select',
    options: enumToOptions(EGender, EGenderLabel),
    enum: EGenderLabel,
  },
  {
    key: 'blood_type',
    label: 'Golongan Darah',
    value: individual?.props.bloodType ?? null,
    disabled: false,
    type: 'select',
    options: [
      { value: '1', label: 'A' },
      { value: '2', label: 'B' },
      { value: '3', label: 'AB' },
      { value: '4', label: 'O' },
    ],
    enum: EBloodTypeLabel,
  },
  {
    key: 'agama_int',
    label: 'Agama',
    value: individual?.props.religion ?? null,
    disabled: false,
    type: 'select',
    options: enumToOptions(EReligion, EReligionLabel),
    enum: EReligionLabel,
  },
  {
    key: 'citizenship',
    label: 'Kewarganegaraan',
    value: individual?.props.citizenship,
    disabled: false,
    type: 'text',
  },
  {
    key: 'marital_status',
    label: 'Status Perkawinan',
    value: individual?.props.maritalStatus ?? null,
    disabled: false,
    type: 'select',
    options: enumToOptions(EMaritalStatus, EMaritalStatusLabel),
    enum: EMaritalStatusLabel,
  },
  {
    key: 'identity_address',
    label: 'Alamat',
    value: individual?.props.identityAddress,
    disabled: false,
    type: 'text',
  },
];
